import React, { useState } from 'react'
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  Chip,
  Stack,
  Divider,
  Paper,
  useTheme
} from '@mui/material'
import {
  ConfirmationNumber,
  Email,
  Comment,
  AttachFile,
  History,
  CheckCircle,
  Info,
  ArrowForward
} from '@mui/icons-material'

interface HowToStep {
  title: string
  icon: React.ReactNode
  description: string
  steps?: string[]
  tips?: string[]
}

const howToSteps: HowToStep[] = [
  {
    title: 'Creating a Ticket',
    icon: <ConfirmationNumber />,
    description: 'There are two ways to create a support ticket:',
    steps: [
      'Click the "Create Ticket" button in your portal',
      'Fill in the ticket details (title, description, category, priority)',
      'Add any relevant file attachments (screenshots, documents, etc.)',
      'Click "Submit" - you\'ll receive a confirmation with your ticket number'
    ],
    tips: [
      'Use a clear, descriptive title so our team can quickly understand your issue',
      'Include as much detail as possible in the description',
      'Screenshots are very helpful for technical issues'
    ]
  },
  {
    title: 'Creating a Ticket via Email',
    icon: <Email />,
    description: 'You can also create tickets by sending an email:',
    steps: [
      'Send an email to your support email address',
      'Use a clear subject line - this becomes the ticket title',
      'Write your issue description in the email body',
      'Attach any relevant files',
      'You\'ll receive an automatic reply with your ticket number'
    ],
    tips: [
      'Save the support email address in your contacts for quick access',
      'Any replies to the email thread will be added as comments to your ticket'
    ]
  },
  {
    title: 'Tracking Your Tickets',
    icon: <History />,
    description: 'You can monitor the progress of all your tickets:',
    steps: [
      'Go to "My Tickets" to see all tickets you\'ve created',
      'Tickets are organized by status: Open, In Progress, On Hold, Resolved',
      'Click on any ticket to view full details and updates',
      'You\'ll see who\'s assigned to your ticket and any status changes'
    ],
    tips: [
      'Check the "Last Updated" time to see recent activity',
      'Filter tickets by status or search by ticket number',
      'You can see the complete history of your ticket in the timeline view'
    ]
  },
  {
    title: 'Adding Comments',
    icon: <Comment />,
    description: 'Communicate with the support team through comments:',
    steps: [
      'Open the ticket you want to comment on',
      'Scroll to the "Comments" section',
      'Type your message in the comment box',
      'Click "Add Comment" to submit',
      'The support team will be notified of your comment'
    ],
    tips: [
      'Use comments to provide additional information or updates',
      'You\'ll receive email notifications when the team replies',
      'All comments are saved in the ticket history'
    ]
  },
  {
    title: 'Attaching Files',
    icon: <AttachFile />,
    description: 'Share files with the support team:',
    steps: [
      'When creating a ticket or adding a comment, look for the attachment option',
      'Click "Choose File" or drag and drop files',
      'You can attach documents, images, logs, or other relevant files',
      'Files are securely stored and only visible to you and the support team'
    ],
    tips: [
      'Common file types: screenshots (.png, .jpg), documents (.pdf, .docx), logs (.txt, .log)',
      'Maximum file size is typically 10MB per attachment',
      'Multiple files can be attached to a single ticket'
    ]
  },
  {
    title: 'Understanding Ticket Status',
    icon: <CheckCircle />,
    description: 'Your ticket moves through different statuses:',
    steps: [
      '🔵 Open - Ticket has been created and is waiting to be assigned',
      '🟡 In Progress - Someone is actively working on your issue',
      '🟠 On Hold - Waiting for information or external dependencies',
      '🟢 Resolved - Issue has been fixed and ticket is closed'
    ],
    tips: [
      'You\'ll receive email notifications when status changes',
      'If a ticket is "On Hold", check if the team needs more information from you',
      'Resolved tickets can be reopened if the issue returns'
    ]
  }
]

export default function HowToGuide() {
  const theme = useTheme()
  const [expandedSection, setExpandedSection] = useState<number | null>(0)

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
          How to Use Your Support Portal
        </Typography>
        <Typography variant="body1" color="text.secondary">
          A quick guide to creating tickets and getting the help you need
        </Typography>
      </Box>

      {/* Quick Tips Alert */}
      <Alert severity="info" icon={<Info />} sx={{ mb: 4 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
          Quick Start
        </Typography>
        <Typography variant="body2">
          Need help right away? Click <strong>"Create Ticket"</strong> in the menu or send an email to your support address.
          You'll get a ticket number and we'll start working on your issue immediately.
        </Typography>
      </Alert>

      {/* Main Content Grid */}
      <Grid container spacing={3}>
        {howToSteps.map((step, index) => (
          <Grid item xs={12} key={index}>
            <Card
              sx={{
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                border: expandedSection === index ? `2px solid ${theme.palette.primary.main}` : '1px solid',
                borderColor: expandedSection === index ? 'primary.main' : 'divider',
                '&:hover': {
                  boxShadow: theme.shadows[4],
                  transform: 'translateY(-2px)'
                }
              }}
              onClick={() => setExpandedSection(expandedSection === index ? null : index)}
            >
              <CardContent>
                {/* Section Header */}
                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      bgcolor: theme.palette.primary.main,
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    {React.cloneElement(step.icon as React.ReactElement, { sx: { fontSize: 28 } })}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      {step.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {step.description}
                    </Typography>
                  </Box>
                  <Chip
                    label={expandedSection === index ? 'Hide' : 'Show Details'}
                    size="small"
                    color={expandedSection === index ? 'primary' : 'default'}
                  />
                </Stack>

                {/* Expanded Content */}
                {expandedSection === index && (
                  <Box sx={{ mt: 2 }}>
                    <Divider sx={{ mb: 2 }} />

                    {/* Steps */}
                    {step.steps && step.steps.length > 0 && (
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                          How to do it:
                        </Typography>
                        <List dense>
                          {step.steps.map((stepItem, stepIndex) => (
                            <ListItem key={stepIndex} sx={{ alignItems: 'flex-start' }}>
                              <ListItemIcon sx={{ minWidth: 36, mt: 0.5 }}>
                                <Box
                                  sx={{
                                    width: 24,
                                    height: 24,
                                    borderRadius: '50%',
                                    bgcolor: theme.palette.primary.light,
                                    color: 'white',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '0.75rem',
                                    fontWeight: 'bold'
                                  }}
                                >
                                  {stepIndex + 1}
                                </Box>
                              </ListItemIcon>
                              <ListItemText
                                primary={stepItem}
                                primaryTypographyProps={{ variant: 'body2' }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    )}

                    {/* Tips */}
                    {step.tips && step.tips.length > 0 && (
                      <Paper sx={{ p: 2, bgcolor: 'success.lighter', border: '1px solid', borderColor: 'success.light' }}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                          <CheckCircle sx={{ color: 'success.main', fontSize: 20 }} />
                          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'success.dark' }}>
                            Helpful Tips:
                          </Typography>
                        </Stack>
                        <List dense sx={{ pl: 1 }}>
                          {step.tips.map((tip, tipIndex) => (
                            <ListItem key={tipIndex} sx={{ py: 0.5, alignItems: 'flex-start' }}>
                              <ListItemIcon sx={{ minWidth: 24, mt: 0.5 }}>
                                <ArrowForward sx={{ fontSize: 16, color: 'success.main' }} />
                              </ListItemIcon>
                              <ListItemText
                                primary={tip}
                                primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Paper>
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* What You Can See Section */}
      <Card sx={{ mt: 4, bgcolor: 'primary.lighter', border: '1px solid', borderColor: 'primary.light' }}>
        <CardContent>
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <Info sx={{ color: 'primary.main', fontSize: 28, mt: 0.5 }} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, color: 'primary.dark' }}>
                What You Can See
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                In your portal, you'll see all tickets created by anyone at your location. This helps your team
                stay informed about ongoing issues and avoid creating duplicate tickets.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                However, you won't see tickets from other locations or departments - those are kept separate
                for privacy and organization.
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* Need More Help Section */}
      <Card sx={{ mt: 4, textAlign: 'center', bgcolor: 'grey.50' }}>
        <CardContent sx={{ py: 4 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            Need More Help?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            If you have questions about using the portal or need technical assistance,
            create a ticket and our support team will be happy to help!
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You can also reach out via email at your designated support address.
          </Typography>
        </CardContent>
      </Card>
    </Container>
  )
}
